import { FC, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import AuthSubHeader from "src/components/authSubHeader";
import CaseInformation from "src/components/caseInformation";
import CaseNotes from "src/components/caseNotes";
import DocumentUpload from "src/components/documentUpload";
import FooterText from "src/components/footerText";
import InsuranceInformation from "src/components/insuranceInformation";
import PatientInformation from "src/components/patientInformation";
import PrescriberInformation from "src/components/prescriberInformation";
import DetailPageInfo from "src/components/detailPageInfo";

import useFetch from "src/hooks/useFetch";
import { AuthContext } from "src/context/authContext";
import { LoaderContext } from "src/context/loaderContext";

import { CaseNotesType } from "src/components/caseNotes/types";
import { CaseDetailType } from "../../../src/types/caseDetail";
import { CaseDetailAPIResponseType } from "./types";
import { APIEndpoints } from "src/types/apiTypes";
import AuthMaunfacturerSubHeader from "src/components/manufacturer/authManufacturerSubHeader";
import KAMFAMNotes from "src/components/kamfamNotes";
import TreatmentStatus from "src/components/caseDetailComp/treatmentStatus";
import ScheduledTXDate from "src/components/caseDetailComp/scheduled_tx_date";
import RequestDateOfTreatment from "src/components/modals/requestDateOfTreatment";
import CancelTreatment from "src/components/modals/cancelTreatment";
import UploadClinicalInformation from "src/components/uploadClinicalInformation";
import { handleNavigation } from "src/helpers";

const HubCaseDetail: FC = (): JSX.Element => {
  const { caseId } = useParams();
  const { hideLoader } = useContext(LoaderContext);
  const { isManufacturer ,userData} = useContext(AuthContext);
  const [caseNotes, setCaseNotes] = useState<CaseNotesType[]>([]);
  const [caseDetail, setCaseDetail] = useState<CaseDetailType>();
  const [kamfamNotes, setKamFamNotes] = useState<CaseNotesType[]>([]);
  const [showRequestTreatmentModal, setDataForRequestModalTreatment] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number; title: string; bentype: string; investigated_at?: string }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0,
    title: "",
    bentype: "",
    investigated_at: ""
  });
  const [cancelTreatmentModal, setDataForCancelModal] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0
  });
  const [refresh, setRefresh] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { defaultView } = useContext(AuthContext);


  const { data: caseDetailObject, error: caseDetailError ,mutate:caseDetailMutate} =
    useFetch<CaseDetailAPIResponseType>(
      APIEndpoints.CASE_DETAIL,
      {},
      { caseId }
    );

  useEffect(() => {
    if (caseDetailObject || caseDetailError) {
      hideLoader();
      if (caseDetailObject) {
        forceRerender()
        setCaseNotes(caseDetailObject?.data.case_notes);
        setCaseDetail(caseDetailObject?.data);
        caseDetailObject && caseDetailObject.data && caseDetailObject.data.kam_fam_notes
          && caseDetailObject.data.kam_fam_notes.notes &&
          setKamFamNotes(caseDetailObject.data.kam_fam_notes.notes);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseDetailObject, caseDetailError,caseDetail]);


  const retriggerCaseDetail = () =>{
    caseDetailMutate()
  }
  const forceRerender = () => setRefresh(prev => prev + 1);

  const redirectDecision = () => {
    const isRedirectRequired = handleNavigation(defaultView, location.pathname);
    if (isRedirectRequired) {
      navigate(defaultView.includes("/") ? defaultView : `/${defaultView}`, { replace: true })
    } else {
      caseDetailMutate({}, { caseId })
    }
  }



  return (
    <div className={"container-fluid overflow-auto"}>
      <div
        className={`${isManufacturer ? `` : `container container-short`
          }  d-flex flex-column flex-fill`}
      >
        {isManufacturer ? <AuthMaunfacturerSubHeader /> : <AuthSubHeader />}

        <div className="container-fluid mt-3">
          <div className="row align-items-center">
            <div className="col-12 col-md-6 align-bottom ps-0 mb-2 mb-md-0">
              <h3 className="text-success fs-2 mb-0"> Case # {caseId} </h3>
            </div>

            <div
              className={`col-12 col-md-6 text-center py-1 background-color-${caseDetail && caseDetail.case_information
                  ? caseDetail.case_information.color
                  : 1
                }`}
            >
              <p className="text-white fs-3 mb-0">
                {caseDetail && caseDetail.case_information
                  ? caseDetail.case_information.status
                  : ""}
              </p>
            </div>
          </div>
        </div>

        {caseDetail ? (
          <>
            {/* Case Information */}
            <div className="mb-2 mt-2">
              <CaseInformation caseObject={caseDetail} />
            </div>

            {/* Benefits Summary */}
            <div className="my-2">
              <DetailPageInfo
                isCasePage={true}
                benefit_summary={caseDetail.benefit_summary}
                coverage_summary={caseDetail.coverage_summary}
                caseObject={caseDetail}
              />
            </div>

            {/* Case Notes */}
            <div className="my-2">
              <CaseNotes
                caseNotes={caseNotes}
                setNotes={setCaseNotes}
                caseObject={caseDetail}
              />
            </div>

            {/* Treatment Statuses */}
            <div className="my-2">
              <TreatmentStatus
                setDataForCancelModal={setDataForCancelModal}
                showHideModalForTreatment={setDataForRequestModalTreatment}
                refreshAPICall={() => caseDetailMutate({}, { caseId })}
                case_id={caseDetail.case_information.case_id}
                prescription_id={caseDetail.case_information.prescription_id}
                investigated_at={caseDetail.case_information?.investigated_at || ""}
                active_precert_id={caseDetail.case_information.active_precert_id}
                appeal_precert_id={caseDetail.case_information.appeal_precert_id}
                pending_precert_id={caseDetail.case_information.pending_precert_id}
                next_Steps={caseDetail.treatment_status}
                pa_workflow_required={caseDetail.case_information?.pa_workflow_required || false}
              />
            </div>

            {/* KAM/FAM Note */}
            {isManufacturer && userData?.ref_type !== "rep" && 
              <div className="my-2">
                <KAMFAMNotes
                  caseId={caseDetail.case_information.case_id}
                  kamfamNotes={kamfamNotes}
                  add_note={caseDetail.kam_fam_notes.add_note}
                  updateKamfamNotes={setKamFamNotes}
                  refreshListing={() => caseDetailMutate({}, { caseId })}
                />
              </div>
             }

            {/* Scheduled Date of Service */}
            <div className="my-2">
              <ScheduledTXDate caseId={caseId ? Number(caseId) : 0} scheduled_tx_date={caseDetail.scheduled_tx_date} />
            </div>

              {!isManufacturer  && 
              <div className="my-2">
                <KAMFAMNotes
                  caseId={caseDetail.case_information.case_id}
                  kamfamNotes={kamfamNotes}
                  add_note={caseDetail.kam_fam_notes.add_note}
                  updateKamfamNotes={setKamFamNotes} 
                  refreshListing={() => caseDetailMutate({}, { caseId })}
                />
              </div>
             }

           {!isManufacturer && caseDetailObject?.data?.clinical_upload_card && <div className="my-2">
              <UploadClinicalInformation  
                caseId={caseDetail.case_information.case_id}
                refreshCall={retriggerCaseDetail}
              />
            </div>}

            {/* Documents */}
            <div className="my-2">
              <DocumentUpload 
                key = {refresh}
                setNotes={setCaseNotes}
                caseDocuments={
                  caseDetail.case_documents ? caseDetail.case_documents : []
                }
                caseId={
                  caseDetail.case_information
                    ? caseDetail.case_information.case_id
                    : 0
                }
                refreshCall={retriggerCaseDetail}
              />
            </div>

            {/* Patient Information */}
            {(!isManufacturer || caseDetail?.phi_eligible_check_fe) &&
              <div className="my-2">
                <PatientInformation caseObject={caseDetail} />
              </div>
            }

            {/* Prescriber Information */}
            <div className="my-2">
              <PrescriberInformation caseObject={caseDetail} />
            </div>

            {/* Insurance Information */}
            <div className="card my-2 rounded-1">
              <div className="card-header bg-success-subtle">
                <h4 className="mb-0 text-black">Insurance Information</h4>
              </div>

              <div className="card-body patient-info-details">
                {caseDetail.insurance_information.length > 0
                  ? caseDetail.insurance_information.map((item, index) => (
                    <InsuranceInformation
                      key={index}
                      caseId={caseDetail.case_information.case_id}
                      insuranceNumber={index + 1}
                      insuranceInformation={item}
                    />
                  ))
                  : null}
              </div>
            </div>
          </>
        ) : null}

        <FooterText />
        {showRequestTreatmentModal.isModalVisible && (
          <RequestDateOfTreatment
            isModalVisible={showRequestTreatmentModal.isModalVisible}
            caseId={showRequestTreatmentModal.caseId}
            prescriptionId={showRequestTreatmentModal.prescriptionId}
            investigated_at={showRequestTreatmentModal.investigated_at}
            hideModal={() => {
              setDataForRequestModalTreatment({
                isModalVisible: false,
                caseId: 0,
                prescriptionId: 0,
                title: "",
                bentype: "",
                investigated_at: ""
              });
            }}
            refreshListing={() => redirectDecision()}
            bentype={showRequestTreatmentModal.bentype}
          />
        )}

        <CancelTreatment
          isModalVisible={cancelTreatmentModal.isModalVisible}
          caseId={cancelTreatmentModal.caseId}
          prescriptionId={cancelTreatmentModal.prescriptionId}
          hideModal={() => {
            setDataForCancelModal({
              isModalVisible: false,
              caseId: 0,
              prescriptionId: 0
            });
          }}
          refreshListing={() => redirectDecision()}
        />
      </div>
    </div>
  );
};

export default HubCaseDetail;
